import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const StatusPage = () => {
  const location = useLocation();
  const initialCaseNumber = location.state?.caseNumber || '';
  const [caseNumber, setCaseNumber] = useState(initialCaseNumber);
  const [caseData, setCaseData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return '#ffc107';
      case 'Success': return '#28a745';
      case 'Denied': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!caseNumber.trim()) {
      setError('Please enter a Tracking Number.');
      setCaseData(null);
      return;
    }

    setLoading(true);
    setError('');
    setCaseData(null);

    try {
      const response = await fetch(API_ENDPOINTS.CASE_BY_NUMBER(caseNumber.trim()), {
        credentials: 'include'
      });

      const contentType = response.headers.get('content-type') || '';
      const data = contentType.includes('application/json')
        ? await response.json()
        : await response.text();

      if (response.ok) {
        setCaseData(data);
        setError('');
      } else {
        const errorMessage = typeof data === 'string'
          ? data
          : data.error || data.message;
        setError(errorMessage || 'Case not found. Please check your Tracking Number and try again.');
        setCaseData(null);
      }
    } catch {
      setError('Error connecting to server. Please try again later.');
      setCaseData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="status-page" style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
      <h1 style={{ textAlign: 'center', color: '#fffdfd', marginBottom: '30px' }}>Check Case Status</h1>

      <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', marginBottom: '30px' }}>
        <p style={{ marginBottom: '15px', fontSize: '16px' }}>
          Enter your tracking number to check the status of your DUI First Time Offender Program case:
        </p>

        {!initialCaseNumber && (
          <div style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '5px',
            padding: '12px',
            marginBottom: '20px'
          }}>
            <p style={{ margin: 0, color: '#856404' }}>
              💡 <strong>Tip:</strong> Your Tracking Number was provided after submitting your application.
            </p>
          </div>
        )}

        <form onSubmit={handleSearch}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              type="text"
              value={caseNumber}
              onChange={(e) => setCaseNumber(e.target.value)}
              placeholder="Enter your tracking number"
              style={{
                flex: 1,
                padding: '12px',
                border: '2px solid #ced4da',
                borderRadius: '5px',
                fontSize: '14px',
                fontFamily: 'monospace'
              }}
              required
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 24px',
                backgroundColor: loading ? '#6c757d' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              {loading ? 'Searching...' : 'Check Status'}
            </button>
          </div>
        </form>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: '5px',
          padding: '15px',
          marginBottom: '20px',
          color: '#721c24'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {caseData && (
        <div style={{
          border: '2px solid #e9ecef',
          borderRadius: '10px',
          backgroundColor: 'white',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            backgroundColor: '#343a40',
            color: 'white',
            padding: '20px',
            borderRadius: '8px 8px 0 0'
          }}>
            <h2 style={{ margin: 0, textAlign: 'center' }}>Case Information</h2>
          </div>

          <div style={{ padding: '30px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
              <div>
                <h3 style={{ color: '#495057', marginBottom: '15px', borderBottom: '2px solid #e9ecef', paddingBottom: '5px' }}>
                  Personal Information
                </h3>
                <div style={{ lineHeight: '1.8' }}>
                  <p><strong>Name:</strong> {caseData.firstName} {caseData.middleName ? `${caseData.middleName} ` : ''}{caseData.lastName}</p>
                  <p><strong>Email:</strong> {caseData.email || '-'}</p>
                </div>
              </div>

              <div>
                <h3 style={{ color: '#495057', marginBottom: '15px', borderBottom: '2px solid #e9ecef', paddingBottom: '5px' }}>
                  Case Details
                </h3>
                <div style={{ lineHeight: '1.8' }}>
                  <p><strong>Tracking Number:</strong> <code style={{ backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '3px' }}>{caseData.caseNumber}</code></p>
                  <p><strong>File Number:</strong> <code style={{ backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '3px' }}>{caseData.fileNumber || '-'}</code></p>
                  <p><strong>Jail ID:</strong> {caseData.jailId || '-'}</p>
                  <p><strong>Police Report Number:</strong> {caseData.policeReportNumber || '-'}</p>
                </div>
              </div>
            </div>

            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              {caseData.status === 'Success' && (
                <div style={{
                  marginBottom: '20px',
                  padding: '20px',
                  backgroundColor: '#d4edda',
                  border: '2px solid #28a745',
                  borderRadius: '10px',
                  boxShadow: '0 4px 8px rgba(40, 167, 69, 0.3)'
                }}>
                  <h2 style={{
                    color: '#155724',
                    margin: '0 0 10px 0',
                    fontSize: '28px',
                    fontWeight: 'bold'
                  }}>
                    Congratulations!
                  </h2>
                  <p style={{
                    color: '#155724',
                    margin: 0,
                    fontSize: '16px',
                    fontWeight: '500'
                  }}>
                    Your DUI First Time Offender Program application has been successfully processed.
                  </p>
                </div>
              )}

              {/* Show denial messaging with next-step guidance when a case is denied. */}
            {/* {caseData.status === 'Denied' && (
                <div style={{
                  marginBottom: '20px',
                  padding: '20px',
                  backgroundColor: '#f8d7da',
                  border: '2px solid #dc3545',
                  borderRadius: '10px',
                  boxShadow: '0 4px 8px rgba(220, 53, 69, 0.2)'
                }}>
                  <h2 style={{
                    color: '#721c24',
                    margin: '0 0 10px 0',
                    fontSize: '28px',
                    fontWeight: 'bold'
                  }}>
                    Update Required
                  </h2>
                  <p style={{
                    color: '#721c24',
                    margin: 0,
                    fontSize: '16px',
                    fontWeight: '500'
                  }}>
                    Your application was denied. Please contact the program office if you need more information.
                  </p>
                </div>
              )}
               */}

              <div style={{ marginBottom: '10px' }}>
                <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>Current Status:</span>
              </div>
              <div style={{
                display: 'inline-block',
                padding: '15px 30px',
                borderRadius: '25px',
                backgroundColor: getStatusColor(caseData.status),
                color: 'white',
                fontSize: '24px',
                fontWeight: 'bold',
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
              }}>
                {caseData.status}
              </div>

              {caseData.status === 'Denied' && (
                <div style={{
                  marginTop: '20px',
                  padding: '20px',
                  backgroundColor: '#f8d7da',
                  border: '2px solid #dc3545',
                  borderRadius: '10px',
                  boxShadow: '0 4px 8px rgba(220, 53, 69, 0.3)'
                }}>
                  {/* <h3 style={{
                    color: '#721c24',
                    margin: '0 0 15px 0',
                    fontSize: '20px',
                    fontWeight: 'bold'
                  }}>
                    Contact Information
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '15px',
                    textAlign: 'left'
                  }}>
                    <div style={{
                      padding: '12px',
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      borderRadius: '8px',
                      border: '1px solid #f5c6cb'
                    }}>
                      <div style={{
                        fontWeight: 'bold',
                        color: '#721c24',
                        marginBottom: '5px',
                        fontSize: '14px'
                      }}>
                        Email
                      </div>
                      <a
                        href="mailto:DUIProgram@coj.net"
                        style={{
                          color: '#007bff',
                          textDecoration: 'none',
                          fontSize: '16px',
                          fontWeight: '500'
                        }}
                        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                      >
                        DUIProgram@coj.net
                      </a>
                    </div>
                    <div style={{
                      padding: '12px',
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      borderRadius: '8px',
                      border: '1px solid #f5c6cb'
                    }}>
                      <div style={{
                        fontWeight: 'bold',
                        color: '#721c24',
                        marginBottom: '5px',
                        fontSize: '14px'
                      }}>
                        Phone
                      </div>
                      <a
                        href="tel:+19046301200"
                        style={{
                          color: '#007bff',
                          textDecoration: 'none',
                          fontSize: '16px',
                          fontWeight: '500'
                        }}
                        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                      >
                        (904) 630-1200
                      </a>
                    </div>
                  </div>
                  <p style={{
                    color: '#721c24',
                    margin: '15px 0 0 0',
                    fontSize: '14px',
                    fontStyle: 'italic',
                    textAlign: 'center'
                  }}>
                    Please contact us during business hours: Monday - Friday, 8:00 AM - 5:00 PM
                  </p> */}
                </div>
              )}
            </div>

            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#d1ecf1', borderRadius: '5px', border: '1px solid #bee5eb' }}>
              <p style={{ margin: 0, color: '#0c5460', fontSize: '14px', textAlign: 'center' }}>
                <strong>Note:</strong> If you have questions about your case status, please contact the State Attorney's Office.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusPage;